// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useEffect } from "react";
import { sGetRaw, sSetRaw } from "../lib/scopedStorage";
import { useAuth } from "../hooks/useAuth";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";
import OELogo from "./OELogo";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

type Mode = "login" | "signup" | "forgot" | "new-password" | "request";

export default function AuthPage({
  initialMode = "login",
  initialEmail = "",
  onClose,
}: {
  initialMode?: "login" | "signup" | "new-password";
  initialEmail?: string;
  onClose?: () => void;
}) {
  const { signIn, signUp, signInWithGoogle, resetPassword, updatePassword } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  // Request Early Access state
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqMsg, setReqMsg] = useState("");
  const [reqState, setReqState] = useState<"idle" | "sending" | "done" | "error">("idle");

  // Read theme preference from localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try { return sGetRaw("oe_dark_mode") !== "false"; } catch { return true; }
  });
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;

  // Responsive breakpoints
  const [winW, setWinW] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1440);
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = winW < 640;
  const isTablet = winW < 1024;
  const hPad = isMobile ? "16px" : isTablet ? "24px" : "32px";

  // Detect signup link from hash (recovery links are handled by useAuth → App.tsx)
  useEffect(() => {
    const h = window.location.hash;
    if (h.startsWith("#/signup")) {
      setMode("signup");
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await signIn(email, password);
        if (res.error) setError(res.error);
      } else if (mode === "signup") {
        if (!displayName.trim()) { setError("Please enter your name"); setLoading(false); return; }
        if (password.length < 10) { setError("Password must be at least 10 characters"); setLoading(false); return; }
        const res = await signUp(email, password, displayName);
        if (res.error) setError(res.error);
        // Only show "check your email" when Supabase actually requires
        // confirmation; with autoconfirm the session is live and the app
        // proceeds on its own via onAuthStateChange.
        else if (res.needsConfirmation) setSignupSuccess(true);
      } else if (mode === "forgot") {
        if (!email.trim()) { setError("Please enter your email"); setLoading(false); return; }
        const res = await resetPassword(email);
        if (res.error) setError(res.error);
        else setResetSent(true);
      } else if (mode === "new-password") {
        if (password.length < 10) { setError("Password must be at least 10 characters"); setLoading(false); return; }
        if (password !== confirmPassword) { setError("Passwords don't match"); setLoading(false); return; }
        const res = await updatePassword(password);
        if (res.error) setError(res.error);
        else setPasswordUpdated(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Keyboard: Escape closes the modal ───────────────────────────────────────
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const inputStyle: React.CSSProperties = {
    width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6,
    color: T.textSecondary, padding: "10px 12px", fontSize: 13, fontFamily: FONT, boxSizing: "border-box",
    outline: "none", transition: "border-color .15s",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 6, display: "block",
  };

  // ── Shared card wrapper (plain function, NOT a React component) ─────────────
  // Calling as wrap(...) instead of <Wrapper> avoids the "component defined
  // inside component" trap where React unmounts/remounts on every keystroke.
  const wrap = (children: React.ReactNode): React.ReactElement => {
    if (onClose) {
      return (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(5,8,14,.75)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: isMobile ? 16 : 32, fontFamily: FONT,
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: "#0f1117", border: "1px solid #1e2433",
            borderRadius: 10, padding: isMobile ? "28px 22px" : "40px 44px",
            maxWidth: 420, width: "100%",
            boxShadow: "0 32px 96px rgba(0,0,0,.7), 0 0 0 1px rgba(79,209,197,.06)",
            position: "relative",
          }}>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                position: "absolute", top: 14, right: 14,
                background: "none", border: "none", color: "#4b5563",
                cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 4,
                borderRadius: 6, transition: "color .15s",
              }}
              onMouseEnter={e => (e.currentTarget.style.color = "#e2e4ea")}
              onMouseLeave={e => (e.currentTarget.style.color = "#4b5563")}
            >
              <Icon.X size={12}/>
            </button>
            {children}
          </div>
        </div>
      );
    }
    // Full-screen fallback for password-reset deep links
    return (
      <div style={{
        fontFamily: FONT, background: T.bgApp, color: T.textPrimary,
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        padding: isMobile ? "16px" : "48px", boxSizing: "border-box",
      }}>
        <div style={{
          background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6,
          padding: isMobile ? "28px 20px" : "40px 40px", maxWidth: 420, width: "100%",
        }}>
          {children}
        </div>
        <div style={{ position: "fixed", bottom: 16, right: 16, display: isMobile ? "none" : undefined }}>
          <div onClick={() => { const next = !isDarkMode; setIsDarkMode(next); try { sSetRaw("oe_dark_mode", String(next)); } catch {} }}
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", opacity: 0.5 }}>
            <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: 1, color: T.textMuted, fontFamily: FONT }}>LIGHT</span>
            <div style={{ width: 36, height: 18, borderRadius: 999, background: isDarkMode ? "#3a3d44" : "#c8cad0", position: "relative", transition: "background .25s", flexShrink: 0 }}>
              <div style={{ width: 14, height: 14, borderRadius: 6, background: "#fff", position: "absolute", top: 2, left: isDarkMode ? 20 : 2, transition: "left .25s", boxShadow: "0 1px 2px rgba(0,0,0,.25)" }} />
            </div>
            <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: 1, color: T.textMuted, fontFamily: FONT }}>DARK</span>
          </div>
        </div>
        <div style={{ position: "fixed", bottom: 16, left: 0, right: 0, textAlign: "center", fontSize: 11, color: T.textMuted, opacity: 0.5, pointerEvents: "none", display: isMobile ? "none" : undefined }}>
          © 2026 OpenEstimate. All rights reserved.
        </div>
      </div>
    );
  };

  // ── Success / info screens ───────────────────────────────────────────────────

  if (signupSuccess) return wrap(
    <div style={{ textAlign: "center" }}>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Icon.Envelope size={36}/></div>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.borderAccent, marginBottom: 8 }}>Check your email</div>
      <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
        We sent a confirmation link to <strong style={{ color: T.positive }}>{email}</strong>. Click the link to activate your account.
      </div>
      <button onClick={() => { setSignupSuccess(false); setMode("login"); }}
        style={{ marginTop: 24, background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textTertiary, cursor: "pointer", padding: "8px 20px", borderRadius: 3, fontSize: 12, fontFamily: FONT }}>
        Back to Sign In
      </button>
    </div>
  );

  if (resetSent) return wrap(
    <div style={{ textAlign: "center" }}>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Icon.Envelope size={36}/></div>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.borderAccent, marginBottom: 8 }}>Check your email</div>
      <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
        We sent a password reset link to <strong style={{ color: T.positive }}>{email}</strong>.
      </div>
      <button onClick={() => { setResetSent(false); setMode("login"); }}
        style={{ marginTop: 24, background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textTertiary, cursor: "pointer", padding: "8px 20px", borderRadius: 3, fontSize: 12, fontFamily: FONT }}>
        Back to Sign In
      </button>
    </div>
  );

  if (passwordUpdated) return wrap(
    <div style={{ textAlign: "center" }}>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Icon.CheckCircle size={36}/></div>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.positive, marginBottom: 8 }}>Password updated</div>
      <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
        Your password has been changed. You can now sign in.
      </div>
      <button onClick={() => { setPasswordUpdated(false); setMode("login"); }}
        style={{ marginTop: 24, background: T.borderAccent, border: "none", color: T.textOnGold, cursor: "pointer", padding: "10px 24px", borderRadius: 3, fontSize: 13, fontFamily: FONT, fontWeight: 700 }}>
        Sign In
      </button>
    </div>
  );

  // ── Sign Up ──────────────────────────────────────────────────────────────────

  if (mode === "signup") return wrap(
    <div>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
        <div onClick={onClose} style={{ cursor: onClose ? "pointer" : "default", userSelect: "none" }} title={onClose ? "Back to home" : undefined}>
          <OELogo height={40} circleColor="white" barColor="#4fd1c5" />
        </div>
      </div>
      <div style={{ marginBottom: 24, borderBottom: "1px solid #1e2433", paddingBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e4ea", letterSpacing: "-.01em" }}>Create Account</div>
        <button type="button" onClick={() => { setMode("login"); setError(""); setPassword(""); }}
          style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", fontSize: 12, fontFamily: FONT, transition: "color .15s" }}
          onMouseEnter={e => e.currentTarget.style.color = "#4fd1c5"}
          onMouseLeave={e => e.currentTarget.style.color = "#4b5563"}>
          Sign In instead
        </button>
      </div>
      {initialEmail && (
        <div style={{ background: "#4fd1c522", border: "1px solid #4fd1c544", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#4fd1c5", marginBottom: 16 }}>
          You've been invited — sign up with <strong>{initialEmail}</strong>
        </div>
      )}
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={labelStyle}>Full Name</label>
          <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Alex Bruce" required style={inputStyle}
            onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
        </div>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required style={inputStyle}
            onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
        </div>
        <div>
          <label style={labelStyle}>Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 10 characters" required minLength={10} style={inputStyle}
            onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
        </div>
        {error && (
          <div style={{ background: "#c0392b22", border: "1px solid #c0392b44", borderRadius: 6, padding: "9px 12px", fontSize: 12, color: "#e74c3c" }}>{error}</div>
        )}
        <button type="submit" disabled={loading}
          style={{ background: "#4fd1c5", border: "none", color: "#0a0f1a", cursor: loading ? "wait" : "pointer", padding: "12px 0", borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 700, letterSpacing: ".02em", opacity: loading ? 0.6 : 1, marginTop: 4 }}>
          {loading ? "Creating account…" : "Create Account"}
        </button>
      </form>
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
        <div style={{ flex: 1, height: 1, background: "#1e2433" }} />
        <span style={{ fontSize: 10, color: "#4b5563", letterSpacing: ".1em" }}>OR</span>
        <div style={{ flex: 1, height: 1, background: "#1e2433" }} />
      </div>
      <button onClick={() => signInWithGoogle()}
        style={{ width: "100%", background: "#161b24", border: "1px solid #1e2433", color: "#9ca3af", cursor: "pointer", padding: "10px 0", borderRadius: 6, fontSize: 12, fontFamily: FONT, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "border-color .15s" }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "#4fd1c5"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "#1e2433"}>
        <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.47 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        Continue with Google
      </button>
    </div>
  );

  // ── Forgot password ──────────────────────────────────────────────────────────

  if (mode === "forgot") return wrap(
    <div>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.02em", color: "#e2e4ea" }}>Reset password</div>
        <div style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>Enter your email and we'll send you a reset link.</div>
      </div>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required style={inputStyle}
            onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
        </div>
        {error && <div style={{ background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, borderRadius: 6, padding: "8px 12px", fontSize: 12, color: T.negative }}>{error}</div>}
        <button type="submit" disabled={loading} style={{ background: "#4fd1c5", border: "none", color: "#0a0f1a", cursor: loading ? "wait" : "pointer", padding: "12px 0", borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 700, opacity: loading ? 0.6 : 1, marginTop: 4 }}>
          {loading ? "Sending…" : "Send Reset Link"}
        </button>
      </form>
      <div style={{ textAlign: "center", marginTop: 20 }}>
        <button onClick={() => { setMode("login"); setError(""); }}
          style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", fontSize: 12, fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Icon.ArrowLeft size={13}/> Back to Sign In
        </button>
      </div>
    </div>
  );

  // ── New password (reset link) ────────────────────────────────────────────────

  if (mode === "new-password") {
    if (passwordUpdated) return wrap(
      <div style={{ textAlign: "center" }}>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(79,209,197,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon.Check size={24} color="#4fd1c5" />
          </div>
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#e2e4ea", marginBottom: 8 }}>Password updated!</div>
        <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 24, lineHeight: 1.6 }}>
          Your new password is set. Sign in to continue.
        </div>
        <button onClick={() => { setPasswordUpdated(false); setPassword(""); setConfirmPassword(""); setMode("login"); if (onClose) onClose(); }}
          style={{ background: "#4fd1c5", border: "none", color: "#0a0f1a", cursor: "pointer", padding: "12px 32px", borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 700 }}>
          Sign In
        </button>
      </div>
    );
    return wrap(
      <div>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.02em", color: "#e2e4ea" }}>Choose a new password</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>Minimum 10 characters.</div>
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>New Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 10 characters" required minLength={10} style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
          </div>
          <div>
            <label style={labelStyle}>Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" required style={{
              ...inputStyle,
              borderColor: confirmPassword && confirmPassword !== password ? "#f87171" : confirmPassword && confirmPassword === password ? "#4fd1c5" : (inputStyle as any).borderColor,
            }}
              onFocus={e => e.target.style.borderColor = confirmPassword !== password ? "#f87171" : "#4fd1c5"}
              onBlur={e => e.target.style.borderColor = confirmPassword && confirmPassword !== password ? "#f87171" : confirmPassword && confirmPassword === password ? "#4fd1c5" : T.borderSecondary} />
            {confirmPassword && confirmPassword !== password && (
              <div style={{ fontSize: 11, color: "#f87171", marginTop: 4 }}>Passwords don't match</div>
            )}
          </div>
          {error && <div style={{ background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, borderRadius: 6, padding: "8px 12px", fontSize: 12, color: T.negative }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ background: "#4fd1c5", border: "none", color: "#0a0f1a", cursor: loading ? "wait" : "pointer", padding: "12px 0", borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 700, opacity: loading ? 0.6 : 1, marginTop: 4 }}>
            {loading ? "…" : "Update Password"}
          </button>
        </form>
      </div>
    );
  }

  // ── Request Early Access form ───────────────────────────────────────────────

  if (mode === "request") {
    const handleRequestSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!reqName.trim() || !reqEmail.trim()) return;
      setReqState("sending");
      try {
        const res = await fetch(
          `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/notify-access-request`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: (import.meta as any).env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ name: reqName.trim(), email: reqEmail.trim(), message: reqMsg.trim() }),
          }
        );
        if (!res.ok) throw new Error("Request failed");
        setReqState("done");
      } catch {
        setReqState("error");
      }
    };

    if (reqState === "done") return wrap(
      <div style={{ textAlign: "center" }}>
        <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Icon.Sparkle size={36}/></div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#4fd1c5", marginBottom: 8 }}>Request sent!</div>
        <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 24 }}>
          We've received your request and will be in touch at <strong style={{ color: "#e2e4ea" }}>{reqEmail}</strong> once access is granted.
        </div>
        <button onClick={() => { setMode("login"); setReqState("idle"); }}
          style={{ background: "none", border: `1px solid #1e2433`, color: T.textWeak, cursor: "pointer", padding: "8px 20px", borderRadius: 3, fontSize: 12, fontFamily: FONT }}>
          Back to Sign In
        </button>
      </div>
    );

    return wrap(
      <div>
        <div style={{ marginBottom: 20 }}>
          <button type="button" onClick={() => setMode("login")}
            style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", fontSize: 11, fontFamily: FONT, padding: 0, display: "flex", alignItems: "center", gap: 4, transition: "color .15s" }}
            onMouseEnter={e => e.currentTarget.style.color = "#4fd1c5"}
            onMouseLeave={e => e.currentTarget.style.color = "#4b5563"}>
            <Icon.ArrowLeft size={13}/> Back to Sign In
          </button>
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#e2e4ea", marginBottom: 6 }}>Request Early Access</div>
          <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
            OpenEstimate is currently invite-only. Fill out the form below and we'll be in touch.
          </div>
        </div>
        <form onSubmit={handleRequestSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>Full Name</label>
            <input type="text" value={reqName} onChange={e => setReqName(e.target.value)} placeholder="Alex Bruce" required style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
          </div>
          <div>
            <label style={labelStyle}>Work Email</label>
            <input type="email" value={reqEmail} onChange={e => setReqEmail(e.target.value)} placeholder="you@company.com" required style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
          </div>
          <div>
            <label style={labelStyle}>Message <span style={{ color: "#4b5563", fontWeight: 400 }}>(optional)</span></label>
            <textarea value={reqMsg} onChange={e => setReqMsg(e.target.value)} placeholder="Tell us about your project or company…" rows={3}
              style={{ ...inputStyle, resize: "vertical" as const, lineHeight: 1.5 }}
              onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
          </div>
          {reqState === "error" && (
            <div style={{ background: "#c0392b22", border: "1px solid #c0392b44", borderRadius: 6, padding: "9px 12px", fontSize: 12, color: "#e74c3c" }}>
              Something went wrong. Please try again or email us directly.
            </div>
          )}
          <button type="submit" disabled={reqState === "sending"}
            style={{
              background: "#4fd1c5", border: "none", color: "#0a0f1a",
              cursor: reqState === "sending" ? "wait" : "pointer", padding: "12px 0", borderRadius: 6,
              fontSize: 13, fontFamily: FONT, fontWeight: 700, letterSpacing: ".02em",
              opacity: reqState === "sending" ? 0.6 : 1, marginTop: 4, transition: "opacity .15s",
            }}>
            {reqState === "sending" ? "Sending…" : "Send Request"}
          </button>
        </form>
      </div>
    );
  }

  // ── Main sign-in form ────────────────────────────────────────────────────────

  return wrap(
    <div>
      {/* Logo */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
        <div
          onClick={onClose}
          style={{ cursor: onClose ? "pointer" : "default", userSelect: "none" }}
          title={onClose ? "Back to home" : undefined}
        >
          <OELogo height={40} circleColor="white" barColor="#4fd1c5" />
        </div>
      </div>

      {/* Sign In header */}
      <div style={{ marginBottom: 24, borderBottom: "1px solid #1e2433", paddingBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e4ea", letterSpacing: "-.01em" }}>Sign In</div>
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required style={inputStyle}
            onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
        </div>
        <div>
          <label style={labelStyle}>Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={10} style={inputStyle}
            onFocus={e => e.target.style.borderColor = "#4fd1c5"} onBlur={e => e.target.style.borderColor = T.borderSecondary} />
        </div>

        <div style={{ textAlign: "right", marginTop: -6 }}>
          <button type="button" onClick={() => { setMode("forgot"); setError(""); }}
            style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", fontSize: 11, fontFamily: FONT, transition: "color .15s" }}
            onMouseEnter={e => e.currentTarget.style.color = "#4fd1c5"}
            onMouseLeave={e => e.currentTarget.style.color = "#4b5563"}>
            Forgot password?
          </button>
        </div>

        {error && (
          <div style={{ background: "#c0392b22", border: "1px solid #c0392b44", borderRadius: 6, padding: "9px 12px", fontSize: 12, color: "#e74c3c" }}>
            {error}
          </div>
        )}

        <button type="submit" disabled={loading}
          style={{
            background: "#4fd1c5", border: "none", color: "#0a0f1a",
            cursor: loading ? "wait" : "pointer", padding: "12px 0", borderRadius: 6,
            fontSize: 13, fontFamily: FONT, fontWeight: 700, letterSpacing: ".02em",
            opacity: loading ? 0.6 : 1, marginTop: 4, transition: "opacity .15s",
          }}>
          {loading ? "…" : "Sign In"}
        </button>
      </form>

      {/* Divider */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
        <div style={{ flex: 1, height: 1, background: "#1e2433" }} />
        <span style={{ fontSize: 10, color: "#4b5563", letterSpacing: ".1em" }}>OR</span>
        <div style={{ flex: 1, height: 1, background: "#1e2433" }} />
      </div>

      {/* Google OAuth */}
      <button onClick={() => signInWithGoogle()}
        style={{
          width: "100%", background: "#161b24", border: "1px solid #1e2433", color: "#9ca3af",
          cursor: "pointer", padding: "10px 0", borderRadius: 6, fontSize: 12, fontFamily: FONT,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          transition: "border-color .15s",
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "#4fd1c5"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "#1e2433"}>
        <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        Continue with Google
      </button>

      {/* Sign Up / Request Early Access links */}
      <div style={{ textAlign: "center", marginTop: 24, paddingTop: 20, borderTop: "1px solid #1e2433", display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <span style={{ fontSize: 12, color: "#4b5563" }}>Have an invitation? </span>
          <button type="button"
            onClick={() => { setError(""); setMode("signup"); setPassword(""); setDisplayName(""); }}
            style={{ background: "none", border: "none", color: "#4fd1c5", cursor: "pointer", fontSize: 12, fontFamily: FONT, fontWeight: 600, padding: 0, transition: "opacity .15s" }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
            Create Account →
          </button>
        </div>
        <div>
          <span style={{ fontSize: 12, color: "#4b5563" }}>No invitation? </span>
          <button type="button"
            onClick={() => { setReqName(""); setReqEmail(""); setReqMsg(""); setReqState("idle"); setError(""); setMode("request"); }}
            style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", fontSize: 12, fontFamily: FONT, fontWeight: 500, padding: 0, transition: "color .15s" }}
            onMouseEnter={e => e.currentTarget.style.color = "#9ca3af"}
            onMouseLeave={e => e.currentTarget.style.color = "#4b5563"}>
            Request Early Access →
          </button>
        </div>
      </div>
    </div>
  );
}
